/**
 * Training Documentation Content
 * Extracted from DocsViewer for maintainability
 */

export const indexDoc = {
  title: 'Training Documentation',
  content: `# Engraveca Staff Training Documentation

## Welcome

This training documentation provides comprehensive guides for using the Engraveca Admin Panel. Each module guide covers field references, workflows, and best practices.

---

## System Overview

\`\`\`mermaid
flowchart TB
    subgraph Admin["🖥️ Admin Panel"]
        direction TB
        OM[Order Management]
        EX[Expenses]
        CAT[Catalog]
        SET[Settings]
    end
    
    subgraph OM_Sub["Order Management"]
        Orders[Orders]
        Invoices[Invoices]
        WO[Work Orders]
        Customers[Customers]
    end
    
    subgraph EX_Sub["Expenses"]
        Vendors[Vendors]
        PO[Purchase Orders]
        Bills[Bills]
        Pay[Payments]
    end
    
    OM --> OM_Sub
    EX --> EX_Sub
    
    style Admin fill:#8B7355,color:#fff
    style OM fill:#3b82f6,color:#fff
    style EX fill:#22c55e,color:#fff
    style CAT fill:#f59e0b,color:#fff
    style SET fill:#6b7280,color:#fff
\`\`\`

---

## Quick Start Guide

### Logging In

1. Navigate to the admin panel
2. Enter your email and password
3. Click **"Sign In"**

### Navigation

The Admin Panel is organized into these main sections:

| Section | Purpose |
|---------|---------|
| **Order Management** | Customer orders, invoices, work orders |
| **Expenses** | Vendor management, purchase orders, payments |
| **Catalog** | Products and pricing |
| **Settings** | System configuration |

---

## Complete Business Flow

\`\`\`mermaid
flowchart LR
    subgraph Sales["💰 Sales (Money IN)"]
        C[Customer] --> Q[Quote]
        Q --> O[Order]
        O --> I[Invoice]
        I --> P[Payment]
    end
    
    subgraph Ops["⚙️ Operations"]
        O --> WO[Work Order]
        WO --> PROD[Production]
        PROD --> SHIP[Ship]
    end
    
    subgraph Expense["💸 Expenses (Money OUT)"]
        V[Vendor] --> PO[Purchase Order]
        PO --> B[Bill]
        B --> PAY[Payment]
    end
    
    style C fill:#e8d5c4
    style P fill:#22c55e,color:#fff
    style PAY fill:#ef4444,color:#fff
\`\`\`

---

## Training Modules

Select a module from the sidebar to view detailed training documentation.

---

## Status Color Reference

### Order/OPS Status Flow

\`\`\`mermaid
flowchart LR
    OPEN[🟡 Open] --> IP[🔵 In Progress] --> DONE[🟢 Completed]
    
    style OPEN fill:#fef08a
    style IP fill:#93c5fd
    style DONE fill:#86efac
\`\`\`

| Status | Color | Meaning |
|--------|-------|---------|
| Open | 🟡 Yellow | New, not started |
| In Progress | 🔵 Blue | Currently working |
| Completed | 🟢 Green | Done |

### Invoice Status Flow

\`\`\`mermaid
flowchart LR
    NI[⚪ Not Issued] --> ISS[🟡 Issued] --> SENT[🔵 Sent] --> PAID[🟢 Paid]
    
    style NI fill:#e5e7eb
    style ISS fill:#fef08a
    style SENT fill:#93c5fd
    style PAID fill:#86efac
\`\`\`

| Status | Color | Meaning |
|--------|-------|---------|
| Not Issued | ⚪ Gray | No invoice yet |
| Issued | 🟡 Yellow | Invoice created |
| Sent | 🔵 Blue | Sent to customer |
| Paid | 🟢 Green | Payment received |

### Due Dates
| Status | Color | Meaning |
|--------|-------|---------|
| Future | 🔵 Blue | 7+ days away |
| Soon | 🟡 Yellow | 4-7 days |
| Very Soon | 🟠 Orange | 1-3 days |
| Overdue | 🔴 Red | Past due |

---

## Keyboard Shortcuts

| Shortcut | Action |
|----------|--------|
| Ctrl + K / Cmd + K | Global search |
| Esc | Close modal/cancel |
| Enter | Confirm/submit |

---

*Last Updated: February 2026*
`
};
